import SharedConfig from '@packages/systems/core/constants/SharedConfig';

import {
  getSingularLabel,
  dynContextText,
  dynContextFieldText,
} from '@packages/systems/dynamo/utils/ParamPanelUtils';
import {getItemLabel} from '@packages/shared/utils/ItemUtils';

import type {CollectionRecordType as CollectionType} from '@packages/systems/dynamo/records/CollectionRecord';
import {type CollectionStateType} from '@packages/systems/core/records/CollectionStateType';
import type {Locale} from '@packages/systems/localization-types';

import {
  areReferencesToSameEntity,
  type BindingSource,
  type BindingContextTypeReference,
  type ConsolidatedBindingContext,
} from '@packages/systems/dynamo/gateway';
import type {FauxBindingSource} from '@packages/systems/dynamo/utils/ParamPanelUtils';

const DYN_CONTEXT = SharedConfig.dynamicContextConstant;

export function getItemRefOptions({
  bindingSource,
  parentBindingContext,
  collectionState,
  currentLocale,
}: {
  bindingSource: BindingSource | FauxBindingSource;
  parentBindingContext: ConsolidatedBindingContext | null;
  collectionState: CollectionStateType;
  currentLocale?: Locale | null | undefined;
}) {
  if (bindingSource.type !== 'ItemRef' && bindingSource.type !== 'ItemRefSet') {
    return [];
  }

  const {type: typeReference, legacyReferencedCollectionId = null} =
    bindingSource.meta;

  const dynContextOptions = parentBindingContext
    ? getBindingContextOptions(parentBindingContext, typeReference)
    : [];

  const {collections} = collectionState;
  const referencedLegacyCollection = legacyReferencedCollectionId
    ? // @ts-expect-error - TS2345 - Argument of type 'null' is not assignable to parameter of type 'CollectionRecordType | undefined'.
      collections.get(legacyReferencedCollectionId, null)
    : null;

  const collectionItemOptions = getItemOptions(
    referencedLegacyCollection,
    collectionState,
    currentLocale
  );

  return [...dynContextOptions, ...collectionItemOptions];
}

function getBindingContextOptions(
  bindingContext: ConsolidatedBindingContext,
  typeReference: BindingContextTypeReference
) {
  const singularName = getSingularLabel(bindingContext);
  const bindingSources = bindingContext.getExpandedBindingSources();

  const bindingContextOptions = [];

  if (bindingContext.matchesTypeReference(typeReference)) {
    // Add "current item" option if binding context matches type reference
    bindingContextOptions.push({
      label: dynContextText(singularName),
      value: DYN_CONTEXT,
      lightning: true,
    });
  }

  bindingSources.forEach((source) => {
    if (
      source.path.length === 1 &&
      (source.type === 'ItemRef' || source.type === 'ItemRefSet') &&
      areReferencesToSameEntity(source.meta.type, typeReference)
    ) {
      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
      const slug = source.path[0].slug;
      bindingContextOptions.push({
        label: dynContextFieldText(source.label, source.type, singularName),
        value: `${DYN_CONTEXT}.${slug}`,
        lightning: true,
      });
    }
  });

  return bindingContextOptions;
}

function getItemOptions(
  collection: CollectionType | null,
  collectionState: CollectionStateType,
  currentLocale?: Locale | null
) {
  if (!collection) {
    return [];
  }

  return collectionState
    .itemsFor(collection, currentLocale ? {locale: currentLocale} : undefined)
    .reduce<Array<any>>((acc, item) => {
      acc.push({
        label: getItemLabel(item, collectionState.scheduledItems),
        value: item.get('_id'),
      });
      return acc;
    }, [])
    .sort((a, b) => a.label.localeCompare(b.label));
}
